import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { v4 as uuidv4 } from 'uuid'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  duration?: number // in ms, 0 for persistent
  action?: {
    label: string
    onClick: () => void
  }
}

type ToastInput =
  | string
  | Omit<Toast, 'id'>
  | {
      message: string
      type?: Toast['type']
      title?: string
      duration?: number
      action?: Toast['action']
    }

interface ToastContextType {
  toasts: Toast[]
  addToast: (toast: ToastInput, type?: Toast['type']) => void
  removeToast: (id: string) => void
  clearAllToasts: () => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((toast: ToastInput, legacyType?: Toast['type']) => {
    const normalized: Omit<Toast, 'id'> =
      typeof toast === 'string'
        ? {
            message: toast,
            type: legacyType || 'info',
            title: '',
          }
        : {
            title: toast.title ?? '',
            message: toast.message,
            type: toast.type ?? legacyType ?? 'info',
            duration: toast.duration,
            action: toast.action,
          }

    const newToast: Toast = {
      ...normalized,
      id: uuidv4(),
    }
    
    setToasts((prev) => [...prev, newToast])

    // Auto-remove toast after duration (unless persistent)
    if (normalized.duration !== 0) {
      const duration = normalized.duration ?? 5000 // Default 5 seconds
      setTimeout(() => {
        removeToast(newToast.id)
      }, duration)
    }
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const clearAllToasts = useCallback(() => {
    setToasts([])
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearAllToasts }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
